import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { getPlainMessageText } from 'utils/citations'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { chatIsToRead, findParticipantsByRole, getChatDateFormatted } from 'utils/chats'
import { useDispatch, useSelector } from 'react-redux'
import { messagesActions } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { markChatAsRead } from 'api/chats'
import { generateSrcFromEmail } from 'utils/avatar'
import { useGetSetting } from 'api/settings'
import { clsx } from 'clsx'

export type ChatRowProps = {
  chat: Chat
  selected?: boolean
}

export const ChatRow = ({ chat, selected = false }: ChatRowProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const currentUser = useSelector(settingsSelectors.userDetails)
  const { colors } = useGetSetting()
  const dateTime = useMemo(() => getChatDateFormatted(chat), [chat])

  const onClickChat = useCallback(async () => {
    dispatch(messagesActions.setChatId(chat._id))
    try {
      await markChatAsRead(chat._id)
    } catch (e) {
      console.error({ e })
    }
  }, [chat, dispatch])

  const isToRead = useMemo(() => {
    return chatIsToRead(chat, currentUser)
  }, [chat, currentUser])

  const lastMessagePreview = useMemo(() => {
    return getPlainMessageText(chat.lastMessage?.message ?? '')
  }, [chat.lastMessage?.message])

  const chatImageUrl = useMemo(() => {
    if (chat.responseMode === ChatResponseModeEnum.BOT) {
      return generateSrcFromEmail('bot@bot.com', colors.primary)
    }
    const operator = findParticipantsByRole(chat, ChatRoleEnum.OPERATOR)
    return generateSrcFromEmail(operator?.user?.email)
  }, [chat, colors.primary])

  // chat.name is LLM-generated server-side from the conversation: it can carry markdown, and when
  // the title generator had nothing but a paused action to work with it carries that raw block too.
  const primary = getPlainMessageText(chat.name ?? '') || lastMessagePreview || chat.ticket?.title || t('chat')

  return (
    <button
      type="button"
      onClick={onClickChat}
      aria-current={selected ? 'true' : undefined}
      className={clsx(
        'flex w-full cursor-pointer flex-row items-center gap-2.5 rounded-xl border-0 px-2 py-2.5 text-left',
        'transition-colors duration-150 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid',
        selected ? 'bg-primary/10' : 'bg-transparent hover:bg-muted',
      )}
    >
      <img src={chatImageUrl} alt={chat.responseMode} className={'h-8 w-8 shrink-0 rounded-full bg-muted'} />

      <div className={'flex min-w-0 flex-1 flex-col gap-0.5'}>
        <div className={'flex items-center justify-between gap-2'}>
          <div className={clsx('truncate text-sm text-foreground', isToRead ? 'font-bold' : 'font-medium')}>
            {primary}
          </div>
          {isToRead && <div className={'h-2.5 w-2.5 shrink-0 rounded-full bg-primary'} />}
        </div>

        <div className={'truncate text-xs text-muted-foreground'}>{dateTime}</div>
      </div>
    </button>
  )
}
